/**
 * Time model: every stored date/time is an Asia/Kolkata wall-clock string.
 *   LocalDateTime  "YYYY-MM-DDTHH:mm"   (booking start/end, hold expiry, log times)
 *   LocalDate      "YYYY-MM-DD"         (payment dates, follow-ups, expense dates)
 * IST has a fixed +05:30 offset and no daylight saving, so conversion to an absolute
 * instant (epoch ms) is exact and does not depend on the device's own timezone.
 * All interval arithmetic is done on instants; all calendar logic on LocalDate strings.
 */
export type LocalDateTime = string;
export type LocalDate = string;
export type Instant = number;

export const MINUTE = 60_000;
export const HOUR = 3_600_000;
export const DAY = 86_400_000;
export const IST_OFFSET_MS = 330 * MINUTE;
export const TIMEZONE = 'Asia/Kolkata';

const pad = (n: number) => String(n).padStart(2, '0');
const LDT_RE = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?$/;

function parts(s: string): [number, number, number, number, number] {
  const m = LDT_RE.exec(s);
  if (!m) throw new RangeError(`Invalid date/time "${s}" (expected YYYY-MM-DD or YYYY-MM-DDTHH:mm)`);
  const y = +m[1]!, mo = +m[2]!, d = +m[3]!, h = m[4] ? +m[4] : 0, mi = m[5] ? +m[5] : 0;
  if (mo < 1 || mo > 12 || d < 1 || d > daysInMonth(y, mo - 1) || h > 23 || mi > 59) throw new RangeError(`Invalid date/time "${s}"`);
  return [y, mo, d, h, mi];
}

export function daysInMonth(year: number, month0: number): number {
  return new Date(Date.UTC(year, month0 + 1, 0)).getUTCDate();
}

/** IST wall-clock string → absolute instant. */
export function toInstant(s: LocalDateTime | LocalDate): Instant {
  const [y, mo, d, h, mi] = parts(s);
  return Date.UTC(y, mo - 1, d, h, mi) - IST_OFFSET_MS;
}

/** Absolute instant → IST wall-clock string (minute precision, truncated). */
export function toLocal(t: Instant): LocalDateTime {
  const u = new Date(t + IST_OFFSET_MS);
  return `${u.getUTCFullYear()}-${pad(u.getUTCMonth() + 1)}-${pad(u.getUTCDate())}T${pad(u.getUTCHours())}:${pad(u.getUTCMinutes())}`;
}

export function dateOf(x: Instant | LocalDateTime): LocalDate {
  const s = typeof x === 'number' ? toLocal(x) : x;
  return s.slice(0, 10);
}

export function timeOf(s: LocalDateTime): string {
  return s.length > 10 ? s.slice(11, 16) : '00:00';
}

export function at(date: LocalDate, time: string): LocalDateTime {
  return `${date}T${time}`;
}

/** Start of the IST calendar day containing the date, as an instant. */
export function startOfDay(date: LocalDate): Instant {
  return toInstant(date.slice(0, 10));
}

export function addDays(date: LocalDate, n: number): LocalDate {
  const [y, mo, d] = parts(date.slice(0, 10));
  const u = new Date(Date.UTC(y, mo - 1, d + n));
  return `${u.getUTCFullYear()}-${pad(u.getUTCMonth() + 1)}-${pad(u.getUTCDate())}`;
}

export function addHours(s: LocalDateTime, h: number): LocalDateTime {
  return toLocal(toInstant(s) + h * HOUR);
}

/** Whole calendar days from a to b (b − a), e.g. ('2026-10-03','2026-10-18') → 15. */
export function daysBetween(a: LocalDate, b: LocalDate): number {
  return Math.round((startOfDay(b) - startOfDay(a)) / DAY);
}

/** 0 = Sunday … 6 = Saturday. */
export function dayOfWeek(date: LocalDate): number {
  const [y, mo, d] = parts(date.slice(0, 10));
  return new Date(Date.UTC(y, mo - 1, d)).getUTCDay();
}

export function monthKey(date: LocalDate | LocalDateTime): string {
  return date.slice(0, 7);
}

export function monthRange(year: number, month0: number): { start: LocalDate; end: LocalDate } {
  const start = `${year}-${pad(month0 + 1)}-01`;
  const ny = month0 === 11 ? year + 1 : year, nm = month0 === 11 ? 0 : month0 + 1;
  return { start, end: `${ny}-${pad(nm + 1)}-01` };
}

export function isValidLocal(s: string): boolean {
  try { parts(s); return true; } catch { return false; }
}

/** Real clock. Domain functions never call this; callers pass `now` in so tests are deterministic. */
export function systemNow(): Instant {
  return Date.now();
}
